import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { TenantProfile, UserAccount, UserInput } from '../../../models/userManagement';
import UserDialog from '../dialogs/UserDialog';

interface UsersTabProps {
  users: UserAccount[];
  tenants: TenantProfile[];
  loading: boolean;
  canEdit: boolean;
  onCreateUser: (payload: UserInput) => Promise<void> | void;
  onUpdateUser: (email: string, payload: UserInput) => Promise<void> | void;
}

const UsersTab: React.FC<UsersTabProps> = ({ users, tenants, loading, canEdit, onCreateUser, onUpdateUser }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAccount | undefined>(undefined);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.email.localeCompare(b.email)),
    [users]
  );

  const tenantLookup = useMemo(() => {
    const map = new Map<string, string>();
    tenants.forEach((tenant) => map.set(tenant.id, tenant.name));
    return map;
  }, [tenants]);

  const handleInvite = () => {
    setEditingUser(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (user: UserAccount) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload: UserInput) => {
    if (editingUser) {
      await onUpdateUser(editingUser.email, payload);
    } else {
      await onCreateUser(payload);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Users</Typography>
        {canEdit && (
          <Button variant="contained" onClick={handleInvite}>
            Invite User
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.email} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{user.name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.tenantId ? tenantLookup.get(user.tenantId) || user.tenantId : 'Global'}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {user.roles.map((role) => (
                        <Chip key={role} label={role} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.active ? 'success' : 'default'}
                      variant={user.active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title="Edit user">
                        <IconButton size="small" onClick={() => handleEdit(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No users available.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tenants={tenants}
        initialValues={editingUser}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default UsersTab;
